import {BlueButton} from '@/components/Button/BlueButton';
import {ModaleLayout} from '@/components/Modale/ModaleLayout';
import {useMe, useSocieteCommerciale} from '@/haligator-factory';
import {PanierCommands} from 'ff-common/src/hooks/panier/use-panier-commands';
import {InfoIcon, WarningIcon} from '@chakra-ui/icons';
import {Text, VStack} from '@chakra-ui/layout';
import {Checkbox, HStack, Link, Spinner, Tooltip} from '@chakra-ui/react';
import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {UseHalStateHook, useCommand, useHalState} from '@synako/haligator';
import {
  ChangerModeDeRenduOptionsDesc,
  PaiementOptionDesc,
  PanierValiderOptionsDesc,
  QuantitesInhabituellesEventDesc,
} from 'api-types/ffconnect-hal';
import {useState} from 'react';
import {Trans, useTranslation} from 'react-i18next';
import {useNavigate, useParams} from 'react-router-dom';
import {routes} from 'ff-common';
import {useClientRoute} from '@/hooks/auth/useClientRoute';
import {useDefaultOnError} from '@/hooks/useDefaultOnError';
import {
  HttpError,
  Navigable,
  Problem,
  State,
  isProblem,
} from '@synako/halitrek';
import {useSimpleTextToast} from '@/hooks/useSimpleTextToast';
import {
  CommandeProblem,
  LigneProblem,
  ModeDeRenduDesArticles,
  PanierLogique,
  ValidationAdresseLivraison,
} from 'api-types/ffconnect';
import {QuantitesAnormalesModal} from './QuantitesAnormalesModal';
import {ModaleChangerAdresseLivraison} from './ModaleChangerAdresseLivraison';
import {useClient} from '@/haligator-factory';
import {useHalCollection} from '@synako/haligator';
import {PANIER_PDF_VALIDE_KEY} from '@/backoffice/constants/sessionStorageBO';
import {buildRoute} from '@synako/enhanced-router';

export const PanierRecapValider: React.FC<{
  validerCommand: PanierCommands['valider'];
  verifierQuantites: PanierCommands['verifierQuantites'];
  validerOptions: UseHalStateHook<PanierValiderOptionsDesc>;
  message_post_validation?: string;
  setCommandeProblems: (problems: CommandeProblem[]) => void;
  setLigneProblems: (problems: LigneProblem[]) => void;
  commentaireClient: string | undefined;
  // Ajouts pour gérer l'ouverture de la modale d'adresse si nécessaire
  validationAdresseLivraison: ValidationAdresseLivraison | undefined;
  changerModeDeRenduOptions: UseHalStateHook<ChangerModeDeRenduOptionsDesc>;
  changerModeDeRenduSubmit: PanierCommands['changerModeDeRendu']['submit'];
  codeAdresse: string;
  modeDeRendu?: ModeDeRenduDesArticles;
  paiementOptionNav: Navigable<PaiementOptionDesc>;
  isPanierPdf: boolean;
}> = ({
  validerCommand,
  validerOptions,
  message_post_validation,
  setCommandeProblems,
  setLigneProblems,
  verifierQuantites,
  commentaireClient,
  validationAdresseLivraison,
  changerModeDeRenduOptions,
  changerModeDeRenduSubmit,
  codeAdresse,
  modeDeRendu,
  paiementOptionNav,
  isPanierPdf,
}) => {
  const [isCGVChecked, setCGVChecked] = useState<boolean>(false);
  const navigate = useNavigate();
  const {buildClientRoute} = useClientRoute();
  const {date_panier_actif} = useParams();
  const {t} = useTranslation('common');
  const textToast = useSimpleTextToast();
  const onError = useDefaultOnError(textToast);
  const clientAdressesHook = useHalCollection(
    useClient().follow('adresses_de_livraison'),
  );

  const paiementOption = useHalState(paiementOptionNav);
  const initierPaiementCommand = useCommand(
    paiementOptionNav,
    'c:initier-paiement',
    {onError},
  );
  const doitPayerEnLigne = paiementOption.data?.doit_payer_en_CB === true;

  const soc = useHalState(useSocieteCommerciale());
  const me = useHalState(useMe());

  // Handler d'erreur personnalisé
  interface ValidationErrorData {
    type: string;
    status: number;
    url: string;
    title: string;
    stack: string;
    nb_problemes_bloquants: number;
    commande_problems: CommandeProblem[];
    lignes_problems: LigneProblem[];
    name: string;
    instance: string;
  }

  const handleValidationError = (error: Error): void => {
    if (
      error instanceof Error &&
      error.name === 'Problem(422,unprocessable_command)'
    ) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const validationError = (error as any).body as ValidationErrorData;

      setCommandeProblems(validationError.commande_problems);
      setLigneProblems(validationError.lignes_problems);
    } else {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      onError(error as any);
    }
  };

  // Si le paiement en ligne est requis, on initie le payin et on redirige vers 1P6
  const submitValiderOuPayer = () => {
    const payload = {
      cgv_acceptee: isCGVChecked,
      commentaire_client: commentaireClient,
    };
    if (doitPayerEnLigne) {
      return initierPaiementCommand
        .submitAsync(payload)
        .then((response) => {
          window.location.href = response.data.data.payment_url;
          return null;
        })
        .catch((error: unknown) => {
          // Les erreurs 1P6 n'ont pas la forme `commande_problems` attendue par handleValidationError.
          const detail = isProblem(error)
            ? (error.body.detail ?? error.body.title)
            : undefined;
          textToast(
            detail
              ? t([`error.${detail}`, detail])
              : t('panier.paiement.erreur_initiation'),
            {duration: 30000, icon: 'alert'},
          );
          return null;
        });
    }
    return validerCommand.submitAsync(payload);
  };

  // Suite d'une validation réussie.
  // Panier PDF : retour direct à la liste des imports PDF du back-office,
  // où un toast confirme la validation.
  // Le BO est une autre application, d'où le rechargement complet ; `replace` retire de
  // l'historique la page du panier validé.
  // On ne redirige que si l'utilisateur a accès à l'import PDF
  // (`me.pdf_order_import_enabled`, déduit de la feature config : flag global ou
  // liste des testeurs), sinon il reste sur la page « Merci ».
  const apresValidation = (logique: PanierLogique) => {
    const id_societe_commerciale = soc.data?.id;
    if (
      isPanierPdf &&
      id_societe_commerciale &&
      me.data?.pdf_order_import_enabled
    ) {
      // Marqueur lu (puis supprimé) par la liste des imports PDF pour afficher le toast
      sessionStorage.setItem(PANIER_PDF_VALIDE_KEY, 'true');
      window.location.replace(
        buildRoute(routes.client.backOfficeParsePdfOrder, {
          id_societe_commerciale,
        }),
      );
      return;
    }

    if (message_post_validation) {
      NiceModal.show(PopupMessagePostValidation, {
        message: message_post_validation,
      });
    }
    window.scrollTo({
      top: 0,
      left: 0,
      // behavior: 'smooth', //This cause the window to stop scrolling when the collapse opens
    });

    navigate(
      buildClientRoute(
        routes.client.panier,
        {date_panier_actif},
        {
          derniereAction: 'valide',
          logique,
        },
      ),
    );
  };

  const handleValiderInModal = () => {
    submitValiderOuPayer()
      .then((eventValidation) => {
        if (!eventValidation) return;
        apresValidation(eventValidation.data.data.logique);
      })
      .catch((error: unknown) => {
        if (error instanceof Error) {
          handleValidationError(error);
        } else {
          onError(error as HttpError | Problem);
        }
      });
  };

  const onClick = () => {
    // Si l'adresse doit être validée, on ouvre la modale de sélection et on arrête ici
    if (validationAdresseLivraison === 'a_faire') {
      NiceModal.show(ModaleChangerAdresseLivraison, {
        codeAdresse,
        changerModeDeRenduOptions: changerModeDeRenduOptions.state,
        validateAction: changerModeDeRenduSubmit,
        states: clientAdressesHook.items,
        mode_de_rendu: modeDeRendu ?? 'livraison',
      });
      return;
    }
    verifierQuantites
      .submitAsync({})
      .then((eventQuantites: State<QuantitesInhabituellesEventDesc>) => {
        // si on a des quantités inhabituelles, on les affiche
        const alertes = eventQuantites.data.data.alertes;
        if (alertes.length > 0) {
          NiceModal.show(QuantitesAnormalesModal, {
            alertes,
            onValider: () => {
              handleValiderInModal();
            },
          });
          return null;
        }
        // Sinon on continue avec la validation (ou le paiement) du panier
        return submitValiderOuPayer();
      })
      .then((eventValidation) => {
        // Si on a un eventValidation, c'est qu'on n'a pas eu de quantités anormales
        // et que la validation s'est bien passée (et qu'on n'est pas parti vers 1P6)
        if (eventValidation) {
          apresValidation(eventValidation.data.data.logique);
        }
      })
      .catch((error: unknown) => {
        if (error instanceof Error) {
          handleValidationError(error);
        } else {
          // Type assertion for the error to match the expected type
          onError(error as HttpError | Problem);
        }
      });
  };

  // Vérifie si les CGV doivent être acceptées
  const mustCheckCGV =
    validerOptions.data !== undefined && !validerOptions.data.cgv_acceptee;

  const isSubmitting =
    validerCommand.isSubmitting ||
    verifierQuantites.isSubmitting ||
    initierPaiementCommand.isSubmitting;

  // Désactive le bouton pendant le chargement ou si d'autres conditions sont remplies
  const disable =
    verifierQuantites.isSubmitting ||
    verifierQuantites.isDisabled ||
    (doitPayerEnLigne
      ? initierPaiementCommand.isSubmitting || initierPaiementCommand.isDisabled
      : validerCommand.isSubmitting || validerCommand.isDisabled) ||
    validerOptions.isDisabled ||
    validerOptions.loading ||
    validerOptions.refreshing ||
    paiementOption.loading ||
    !!validerOptions.data?.nb_problemes_bloquants ||
    (!isCGVChecked && mustCheckCGV);

  return (
    <>
      {doitPayerEnLigne && (
        <VStack
          gap='3'
          alignItems='stretch'
          mb='3'
        >
          {/* <Text fontWeight='600'
            fontSize='sm'
            textColor='black'>
            {t('panier.recap.option_de_paiement')}
          </Text> */}
          <HStack
            borderWidth='1px'
            borderColor='gray.400'
            borderRadius='8px'
            p='3'
            alignItems='center'
          >
            {/* <Radio isChecked
              isReadOnly
              colorScheme='blue'
              value='cb' /> */}
            <Text
              textColor='black'
              fontSize='sm'
            >
              {t('panier.recap.paiement_par_carte_bancaire')}
            </Text>
            <Tooltip label={t('panier.recap.tooltip_paiement')}>
              <InfoIcon
                ml='auto'
                color='gray.dark'
              />
            </Tooltip>
          </HStack>
        </VStack>
      )}
      <VStack
        gap='3'
        // Sous lg order -2 place le bloc de validation tout en bas, juste sous l'encart de statut Tolede (order -1).
        order={{base: '-2', lg: 'initial'}}
        alignItems='stretch'
        mt={!mustCheckCGV ? '5' : '0'}
      >
        {mustCheckCGV && (
          <Checkbox
            alignItems='baseline'
            gap='2'
            my='2'
            isChecked={isCGVChecked}
            onChange={(e) => {
              setCGVChecked(e.target.checked);
            }}
          >
            <Text
              textAlign='start'
              textColor='black'
              fontSize='sm'
            >
              <Trans
                t={t}
                components={{
                  Link: (
                    <Link
                      href={soc.data?.url_cgv}
                      target='_blank'
                      textDecorationLine='underline'
                      cursor='pointer'
                    />
                  ),
                }}
              >
                {t('panier.recap.accepter_conditions')}
              </Trans>
              <Text
                as='span'
                textColor='red.warning'
              >
                {' '}
                *
              </Text>
            </Text>
          </Checkbox>
        )}
        <BlueButton
          texte={
            isSubmitting ? (
              <Spinner />
            ) : doitPayerEnLigne ? (
              t('panier.recap.valider_et_payer')
            ) : (
              t('panier.recap.valider_mon_panier')
            )
          }
          onClick={onClick}
          mt='0'
          isDisabled={disable}
        />
      </VStack>
    </>
  );
};

export const PopupMessagePostValidation = NiceModal.create(
  ({message}: {message: string}) => {
    const {t} = useTranslation('common');
    const modal = useModal();
    return (
      <ModaleLayout
        visible={modal.visible}
        hide={modal.remove}
        headerIcon={<WarningIcon />}
        titre={t('global.attention')}
      >
        <HStack
          alignItems={'baseline'}
          bg='blue.light'
        >
          <Text>{message}</Text>
        </HStack>
        <BlueButton
          w='100%'
          mt='10'
          onClick={modal.remove}
          texte={t('global.compris')}
        />
      </ModaleLayout>
    );
  },
);
